import { createFileRoute } from "@tanstack/react-router";
import { handleOAuthCallback } from "lib/auth";
import { getSsoCallbackErrorMessage } from "lib/components/account/ssoCallbackErrorMessage";
import { loginPath } from "lib/routeHelpers";
import { useEffect, useRef, useState } from "react";
import { Helmet as Head } from "react-helmet-async";

function SSOCallbackPage() {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const started = useRef(false);

  useEffect(() => {
    // Guard against React StrictMode double-invocation
    if (started.current) {
      return;
    }
    started.current = true;

    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    const state = params.get("state");
    const error = params.get("error");
    const errorDescription = params.get("error_description");

    if (error) {
      setErrorMessage(getSsoCallbackErrorMessage(error, errorDescription));
      return;
    }

    if (!code || !state) {
      setErrorMessage("Missing authorization code or state. Please try signing in again.");
      return;
    }

    handleOAuthCallback(code, state)
      .then(([redirectTo, notification]) => {
        if (notification) {
          setErrorMessage(getSsoCallbackErrorMessage("callback_error", String(notification.message)));
          return;
        }
        if (redirectTo) {
          window.location.href = redirectTo;
        }
      })
      .catch((e: unknown) => {
        const message = e instanceof Error ? e.message : String(e);
        setErrorMessage(getSsoCallbackErrorMessage("callback_error", message));
      });
  }, []);

  if (errorMessage) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          gap: "16px",
        }}
      >
        <Head>
          <title>Sign-in error</title>
        </Head>
        <p style={{ color: "var(--color-error, #d32f2f)", maxWidth: "400px", textAlign: "center" }}>{errorMessage}</p>
        <p style={{ color: "var(--color-text-secondary, #555)", maxWidth: "400px", textAlign: "center" }}>
          If this persists, contact your administrator or Higharc support.
        </p>
        <a href={loginPath()} style={{ color: "var(--color-primary, #1976d2)" }}>
          Return to login
        </a>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh" }}>
      <Head>
        <title>Signing in…</title>
      </Head>
      <p style={{ color: "var(--color-text-secondary, #555)" }}>Signing you in…</p>
    </div>
  );
}

export const Route = createFileRoute("/account/callback")({
  component: SSOCallbackPage,
  staticData: { isPublicPage: true },
});
